import React, { useState, useEffect, useRef } from 'react';
import { JalurProvider, useJalur } from './JalurContext';
import { ThemeProvider } from './ThemeContext';
import LayarPilihJalur from './LayarPilihJalur';
import ToggleTema from './ToggleTema';
import TombolPushNotif from './TombolPushNotif';
import { aktifkanPushNotif, pushDidukungBrowser } from './pushNotif';
import './theme.css';
import AdminPanel from './AdminPanel';
import SubAdminPanel from './SubAdminPanel';
import KraepelinModul from './KraepelinModul';
import KecermatanModul from './KecermatanModul';
import LogikaModul from './LogikaModul';
import VerbalModul from './VerbalModul';
import KepribadianModul from './KepribadianModul';
import FiguralModul from './FiguralModul';
import ArmyAlphaModul from './ArmyAlphaModul';
import AkademikModul from './AkademikModul';
import MekanikModul from './MekanikModul';
import SubstitusiModul from './SubstitusiModul';
import KestabilanModul from './KestabilanModul';
import InterpretasiGambarModul from './InterpretasiGambarModul';
import InterpretasiBentukModul from './InterpretasiBentukModul';
import { getDeskripsiModul } from './deskripsiJalur';
import ProgressBeranda from './ProgressBeranda';
import GamifikasiCard from './GamifikasiCard';
import InfoRekrutmenBanner from './InfoRekrutmenBanner';
import { INFO_REKRUTMEN } from './infoRekrutmen';
import ProfilShortcut from './ProfilShortcut';
import TryoutScreen, { getUrutanModul, MODUL_DASAR } from './TryoutScreen';
import { modulBaruAktif, MODUL_BARU_IDS } from './modulBaruConfig';
import {
  Home, LayoutGrid, History, User, Calculator, Brain, BookOpen,
  FileText, ClipboardList, ChevronRight,
  Lock, Mail, ArrowLeft, Clock, CheckCircle2, Circle, Award, LogOut, Star,
  Hash, Grid, Download, X, Settings, Puzzle, Shield, Image, Sparkles
} from 'lucide-react';
import { unduhPdf } from './unduhPdf';

const API_URL_LAPORAN = 'https://skortes.com';

const RANKS = [
  { name: 'Pemula', abbr: 'Lv.1', tier: 1 },
  { name: 'Rajin', abbr: 'Lv.2', tier: 2 },
  { name: 'Terampil', abbr: 'Lv.3', tier: 3 },
  { name: 'Cakap', abbr: 'Lv.4', tier: 4 },
  { name: 'Mahir', abbr: 'Lv.5', tier: 5 },
  { name: 'Unggul', abbr: 'Lv.6', tier: 6 },
  { name: 'Master', abbr: 'Lv.7', tier: 7 },
];

const MODULES = [
  {
    id: 'kraepelin',
    name: 'Tes Kraepelin / Pauli',
    short: 'Kraepelin',
    desc: 'Deret penjumlahan angka berurutan untuk mengukur ketelitian, konsistensi, dan daya tahan kerja.',
    icon: Calculator,
    duration: '60 menit',
    status: 'belum',
    features: [
      'Lembar angka digital dengan numpad untuk',
      'Timer otomatis per kolom (90 detik)',
      'Grafik kestabilan & kecepatan kerja',
      'Analisis konsistensi per kolom',
    ],
  },
  {
    id: 'kecermatan',
    name: 'Tes Kecermatan',
    short: 'Kecermatan',
    desc: 'Bandingkan tiga kolom huruf atau angka dan tentukan mana yang sama.',
    icon: CheckCircle2,
    duration: '7-8 menit',
    status: 'belum',
    features: [
      'Format NNC-2 standar seleksi',
      '30 soal acak dari bank soal',
      '15 detik per soal',
      'Skor dan analisis otomatis',
    ],
  },
  {
    id: 'logika',
    name: 'Logika & Numerik',
    short: 'Logika',
    desc: 'Deret angka, aritmatika cerita, dan penalaran numerik setara tes seleksi resmi.',
    icon: Hash,
    duration: '25 menit',
    status: 'belum',
    features: [
      '3 mode: Deret Angka / Aritmatika / Campuran',
      '30 soal per sesi dengan timer 25 menit',
      'Skor Deret & Aritmatika ditampilkan terpisah',
      'Pembahasan jawaban setelah tes selesai',
    ],
  },
  {
    id: 'verbal',
    name: 'Tes Verbal',
    short: 'Verbal',
    desc: 'Sinonim, antonim, dan kata ganjil untuk mengukur kemampuan bahasa dan penalaran verbal.',
    icon: BookOpen,
    duration: '25 menit',
    status: 'belum',
    features: [
      '3 mode: Sinonim & Antonim / Kata Ganjil / Campuran',
      '30 soal per sesi dengan timer 25 menit',
      'Skor tiap kategori ditampilkan terpisah',
      'Pembahasan jawaban setelah tes selesai',
    ],
  },
  {
    id: 'figural',
    name: 'Figural & Spasial',
    short: 'Figural',
    desc: 'Rotasi bangun, bayangan cermin, pola matriks, dan deret gambar untuk mengukur kemampuan spasial.',
    icon: Grid,
    duration: '35 menit',
    status: 'belum',
    features: [
      '3 mode: Rotasi & Bayangan / Pola Matriks / Campuran',
      '50 soal visual interaktif dengan SVG',
      'Skor per kategori ditampilkan terpisah',
      'Pembahasan visual setelah tes selesai',
    ],
  },
  {
    id: 'kepribadian',
    name: 'Tes Kepribadian',
    short: 'Kepribadian',
    desc: 'Kuesioner pilihan ganda untuk memetakan karakter, motivasi, dan kestabilan emosi.',
    icon: Brain,
    duration: '40–60 menit',
    status: 'belum',
    features: [
      'Ratusan butir soal pilihan ganda',
      'Progress tersimpan otomatis bila keluar aplikasi',
      'Profil kepribadian ringkas setelah selesai',
      'Catatan: bukan diagnosis klinis, hanya simulasi latihan',
    ],
  },
  {
    id: 'akademik',
    name: 'Tes Kemampuan Akademik',
    short: 'Akademik',
    desc: 'Soal logika, numerik, dan verbal setara tes potensi akademik (TPA).',
    icon: FileText,
    duration: '60–90 menit',
    status: 'belum',
    features: [
      'Bank soal logika, aritmatika, dan sinonim/antonim',
      'Mode latihan per kategori & mode simulasi penuh',
      'Pembahasan jawaban setelah tes selesai',
      'Statistik kekuatan & kelemahan per kategori',
    ],
  },
  {
    id: 'armyalpha',
    name: 'Tes Instruksi Cepat',
    short: 'Instruksi Cepat',
    desc: 'Tes logika dan instruksi cepat menggunakan simbol, kode, dan gambar.',
    icon: ClipboardList,
    duration: '15–20 menit',
    status: 'belum',
    features: [
      'Instruksi bertahap dengan batas waktu ketat',
      'Soal simbol & kode interaktif',
      'Skor otomatis berdasarkan kecepatan & akurasi',
      'Mode latihan tanpa timer untuk pemula',
    ],
  },
  {
    id: 'mekanik',
    name: 'Tes Bakat Mekanik',
    short: 'Mekanik',
    desc: 'Roda gigi, katrol, tuas, dan bidang miring untuk mengukur pemahaman prinsip mekanika dasar.',
    icon: Settings,
    duration: '15 menit',
    status: 'belum',
    beta: true,
    features: [
      'Soal diagram interaktif (roda gigi, katrol, tuas, dsb)',
      '12 soal per sesi dengan timer 15 menit',
      'Pembahasan prinsip fisika setelah tes selesai',
      'Ikut dihitung ke skor Tryout resmi',
    ],
  },
  {
    id: 'substitusi',
    name: 'Tes Substitusi Simbol',
    short: 'Substitusi',
    desc: 'Cocokkan simbol dengan kode sesuai kunci secepat & seteliti mungkin — mengukur kecepatan kerja.',
    icon: Puzzle,
    duration: '5 menit',
    status: 'belum',
    beta: true,
    features: [
      'Kunci 10 pasangan simbol-kode selalu tampil di layar',
      'Format kerja cepat, timer total 5 menit',
      'Skor akurasi dari jumlah soal yang sempat dikerjakan',
      'Ikut dihitung ke skor Tryout resmi',
    ],
  },
  {
    id: 'kestabilan',
    name: 'Kestabilan Pribadi',
    short: 'Kestabilan',
    desc: 'Kuesioner Ya/Tidak orisinal untuk mengenali kecenderungan stabilitas emosi, toleransi stres, dan disiplin diri.',
    icon: Shield,
    duration: '70 menit',
    status: 'belum',
    beta: true,
    features: [
      '565 pernyataan Ya/Tidak, 8 dimensi kepribadian',
      'Progress tersimpan otomatis bila keluar aplikasi',
      'Ringkasan kecenderungan deskriptif per dimensi',
      'Catatan: bukan diagnosis klinis, hanya latihan pengenalan diri',
    ],
  },
  {
    id: 'interpretasigambar',
    name: 'Interpretasi Gambar',
    short: 'Interpretasi Gambar',
    desc: 'Latihan menulis cerita dari gambar situasi ambigu — siapa tokohnya, kegiatannya apa, dan bagaimana perasaannya.',
    icon: Image,
    duration: '24 menit',
    status: 'belum',
    beta: true,
    features: [
      '8 gambar situasi ambigu orisinal',
      'Contoh jawaban tersedia langsung di tiap soal',
      'Progress tersimpan otomatis bila keluar aplikasi',
      'Catatan: bukan penilaian klinis, isi karangan tidak dinilai sistem',
    ],
  },
  {
    id: 'interpretasibentuk',
    name: 'Interpretasi Bentuk',
    short: 'Interpretasi Bentuk',
    desc: 'Pilihan ganda menebak makna bentuk abstrak simetris — latihan membiasakan format soal interpretasi.',
    icon: Sparkles,
    duration: '12 menit',
    status: 'belum',
    beta: true,
    features: [
      '10 gambar abstrak simetris orisinal',
      'Tidak ada jawaban benar/salah',
      'Ringkasan distribusi pola jawaban non-klinis',
      'Catatan: bukan diagnosis klinis, hanya latihan simulasi',
    ],
  },
];

function getRank(modules) {
  const total = modules.length;
  const done = modules.filter((m) => m.status === 'selesai').length;
  const pct = total === 0 ? 0 : done / total;
  const idx = Math.min(RANKS.length - 1, Math.floor(pct * RANKS.length));
  const current = RANKS[idx];
  const next = RANKS[Math.min(RANKS.length - 1, idx + 1)];
  return { current, next, done, total, pct };
}

function StatusBadge({ status, score }) {
  if (status === 'selesai') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
            style={{ color: 'var(--success)', backgroundColor: 'rgba(74,222,128,0.15)', border: '1px solid rgba(74,222,128,0.3)' }}>
        <CheckCircle2 size={12} /> Selesai · {score}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
          style={{ color: 'var(--text-muted)', backgroundColor: 'var(--bg-card3)', border: '1px solid var(--border)' }}>
      <Circle size={12} /> Belum dikerjakan
    </span>
  );
}

const API_URL = 'https://skortes.com';

function LoginScreen({ onLogin }) {
  // Kalau dibuka lewat link referral (mis. skortes.com/?kode=K3F9XA), langsung
  // arahkan ke form registrasi dengan kode itu sudah terisi otomatis — jadi user
  // yang klik link sub-admin gak perlu tahu/ketik kode sendiri.
  const kodeDariUrl = new URLSearchParams(window.location.search).get('kode');
  const [mode, setMode]           = useState(kodeDariUrl ? 'register' : 'login');
  const [nama, setNama]           = useState('');
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [kodeAkses, setKodeAkses] = useState(kodeDariUrl || '');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [suksesDaftar, setSuksesDaftar] = useState(false);
  const [emailDaftar, setEmailDaftar]   = useState('');
  const [kirimUlang, setKirimUlang]     = useState(false);

  // Jumlah modul ditampilkan dinamis, bukan angka statis, supaya otomatis ikut
  // naik begitu modul baru resmi masuk Tryout (lihat modulBaruConfig.js). Halaman
  // ini publik/pra-login jadi belum ada tryout spesifik yang bisa dicek — dipakai
  // proxy "sekarang sudah lewat tanggal aktifnya modul baru atau belum", yang
  // otomatis konsisten dengan tryout yang dibuat cron mingguan (lihat server.js).
  const totalModulDasar = MODUL_DASAR.length + MODUL_BARU_IDS.length;
  const totalModulTryoutAktif = MODUL_DASAR.length + (modulBaruAktif(new Date()) ? MODUL_BARU_IDS.length : 0);
  const [lupaPassword, setLupaPassword] = useState(false);
  const [emailReset, setEmailReset]     = useState('');
  const [suksesReset, setSuksesReset]   = useState(false);

  const handleLupaPassword = async () => {
    if (!emailReset) return setError('Masukkan email kamu dulu');
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API_URL}/api/lupa-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailReset }),
      });
      const data = await res.json();
      if (data.message) {
        setSuksesReset(true);
      } else {
        setError(data.error || 'Gagal mengirim email reset');
      }
    } catch {
      setError('Tidak bisa terhubung ke server');
    }
    setLoading(false);
  };

  const handleLogin = async () => {
    if (!email || !password) return setError('Email/Username dan password wajib diisi');
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (data.token) {
        // Cek apakah akun sudah diverifikasi
        if (data.user.status === 'pending') {
          setEmailDaftar(email);
          setError('');
          setSuksesDaftar(true);
          setLoading(false);
          return;
        }
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        onLogin(data.user);
      } else {
        setError(data.error || 'Login gagal');
      }
    } catch {
      setError('Tidak bisa terhubung ke server');
    }
    setLoading(false);
  };

  const handleRegister = async () => {
    if (!nama || !email || !password) return setError('Semua kolom wajib diisi');
    if (password.length < 6) return setError('Password minimal 6 karakter');
    if (!kodeAkses) return setError('Kode akses wajib diisi');
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nama, email, password, kode_akses: kodeAkses }),
      });
      const data = await res.json();
      if (data.token) {
        setEmailDaftar(email);
        setSuksesDaftar(true);
      } else {
        setError(data.error || 'Registrasi gagal');
      }
    } catch {
      setError('Tidak bisa terhubung ke server');
    }
    setLoading(false);
  };

  const handleKirimUlang = async () => {
    setKirimUlang(true);
    try {
      await fetch(`${API_URL}/api/kirim-ulang-verifikasi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailDaftar }),
      });
    } catch {}
    setTimeout(() => setKirimUlang(false), 3000);
  };

  // ── Layar sukses daftar / belum verifikasi ─────────────────
  if (suksesDaftar) {
    return (
      <div className="flex flex-col h-full px-7 pt-16 pb-10 bg-[var(--bg-app)] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(135deg, var(--accent) 0px, var(--accent) 1px, transparent 1px, transparent 18px)' }} />
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'var(--bg-card3)', border: '2px solid var(--accent)' }}>
            <span className="text-4xl">✉️</span>
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>
            CEK EMAIL KAMU!
          </h2>
          <p className="text-sm mb-2" style={{ color: 'var(--text-muted)' }}>
            Email verifikasi sudah dikirim ke:
          </p>
          <p className="font-bold mb-6 px-4 py-2 rounded-xl" style={{ color: 'var(--accent)', backgroundColor: 'var(--bg-card3)', border: '1px solid var(--border)' }}>
            {emailDaftar}
          </p>
          <div className="rounded-xl p-4 mb-6 text-left w-full" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
            <p className="text-xs font-bold mb-2" style={{ color: 'var(--accent)' }}>📋 Langkah selanjutnya:</p>
            <ol className="space-y-1.5">
              {[
                'Buka aplikasi email di HP atau laptop',
                'Cari email dari noreply.psikotes@gmail.com',
                'Klik tombol "Verifikasi Email Saya"',
                'Kembali ke app ini dan login',
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-2 text-xs" style={{ color: '#D4CEB8' }}>
                  <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold" style={{ backgroundColor: 'var(--bg-card3)', color: 'var(--accent)' }}>{i+1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </div>
          <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>Email tidak masuk? Cek folder Spam/Promosi</p>
          <button onClick={handleKirimUlang} disabled={kirimUlang}
            className="w-full py-3 rounded-xl text-sm font-semibold mb-3"
            style={{ backgroundColor: kirimUlang ? 'var(--bg-card3)' : 'var(--bg-card2)', color: kirimUlang ? '#4ADE80' : 'var(--accent)', border: '1px solid var(--border)' }}>
            {kirimUlang ? '✅ Email dikirim ulang!' : '🔄 Kirim Ulang Email Verifikasi'}
          </button>
          <button onClick={() => { setSuksesDaftar(false); setMode('login'); }}
            className="w-full py-3 rounded-xl text-sm font-semibold"
            style={{ backgroundColor: 'var(--accent)', color: 'var(--bg-app)', fontFamily: "'Poppins', sans-serif" }}>
            Sudah Verifikasi → Login
          </button>
        </div>
      </div>
    );
  }

  // ── Layar Lupa Password ────────────────────────────────────
  if (lupaPassword) {
    return (
      <div className="flex flex-col h-full px-7 pt-8 pb-6 bg-[var(--bg-app)] relative overflow-y-auto overflow-x-hidden">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(135deg, var(--accent) 0px, var(--accent) 1px, transparent 1px, transparent 18px)' }} />
        <div className="relative z-10 flex flex-col">
          <button onClick={() => { setLupaPassword(false); setError(''); setSuksesReset(false); setEmailReset(''); }}
            className="flex items-center gap-2 text-sm mb-8" style={{ color: 'var(--text-muted)' }}>
            <ArrowLeft size={16} /> Kembali ke Login
          </button>
          {!suksesReset ? (
            <>
              <div className="w-14 h-14 rounded-md bg-[var(--accent)] flex items-center justify-center mb-6">
                <span className="text-2xl">🔐</span>
              </div>
              <h1 className="text-[var(--text-primary)] text-3xl font-bold mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                LUPA PASSWORD?
              </h1>
              <p className="text-stone-400 text-sm mb-8 leading-relaxed">
                Masukkan email yang terdaftar. Kami akan kirimkan link untuk membuat password baru.
              </p>
              {error && (
                <div className="mb-4 px-4 py-3 rounded-xl text-xs" style={{ backgroundColor: '#3A2020', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' }}>
                  {error}
                </div>
              )}
              <div className="mb-4">
                <label className="text-xs uppercase tracking-wider text-stone-500 mb-1.5 block">Email Terdaftar</label>
                <div className="flex items-center gap-2 bg-[var(--bg-card2)] border border-[var(--border)] rounded-lg px-3 py-3">
                  <Mail size={16} className="text-stone-500" />
                  <input type="email" placeholder="nama@email.com" value={emailReset}
                    onChange={e => setEmailReset(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleLupaPassword()}
                    className="bg-transparent outline-none text-[var(--text-primary)] text-sm placeholder-stone-600 w-full" />
                </div>
              </div>
              <button onClick={handleLupaPassword} disabled={loading}
                className="w-full py-3.5 rounded-lg font-bold text-sm uppercase tracking-wide"
                style={{ backgroundColor: 'var(--accent)', color: 'var(--bg-app)', fontFamily: "'Poppins', sans-serif", opacity: loading ? 0.7 : 1 }}>
                {loading ? 'Mengirim...' : '📧 Kirim Link Reset Password'}
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center text-center mt-8">
              <span className="text-5xl mb-4">📧</span>
              <h2 className="text-xl font-bold mb-2" style={{ color: '#4ADE80', fontFamily: "'Poppins', sans-serif" }}>EMAIL TERKIRIM!</h2>
              <p className="text-sm mb-2" style={{ color: 'var(--text-muted)' }}>Link reset password sudah dikirim ke:</p>
              <p className="font-bold mb-6 px-4 py-2 rounded-xl" style={{ color: 'var(--accent)', backgroundColor: 'var(--bg-card3)', border: '1px solid var(--border)' }}>
                {emailReset}
              </p>
              <div className="rounded-xl p-4 mb-4 text-left w-full" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
                <p className="text-xs font-bold mb-2" style={{ color: 'var(--accent)' }}>📋 Langkah selanjutnya:</p>
                <ol className="space-y-1.5">
                  {['Buka email kamu', 'Cari email dari noreply.psikotes@gmail.com', 'Klik tombol "Reset Password Saya"', 'Isi password baru', 'Login dengan password baru'].map((step, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs" style={{ color: '#D4CEB8' }}>
                      <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold" style={{ backgroundColor: 'var(--bg-card3)', color: 'var(--accent)' }}>{i+1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
              <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>Link berlaku 1 jam. Cek folder Spam jika tidak masuk.</p>
              <button onClick={() => { setLupaPassword(false); setSuksesReset(false); setEmailReset(''); }}
                className="w-full py-3 rounded-xl font-bold text-sm uppercase"
                style={{ backgroundColor: 'var(--accent)', color: 'var(--bg-app)', fontFamily: "'Poppins', sans-serif" }}>
                Kembali ke Login →
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full px-7 pt-8 pb-6 bg-[var(--bg-app)] relative overflow-y-auto overflow-x-hidden">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(135deg, var(--accent) 0px, var(--accent) 1px, transparent 1px, transparent 18px)' }} />
      <div className="relative z-10 flex flex-col min-h-full">
        <div className="mt-4">
          <div className="w-14 h-14 rounded-md bg-[var(--accent)] flex items-center justify-center mb-5">
            <Award size={28} className="text-[var(--bg-app)]" />
          </div>
          <h1 className="text-[var(--text-primary)] text-4xl leading-none tracking-wide" style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>SKORTES</h1>
          <h1 className="text-[var(--accent)] text-4xl leading-none tracking-wide mb-3" style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>SIMULASI PSIKOTES</h1>
          <p className="text-stone-400 text-sm leading-relaxed">Latihan terarah untuk berbagai jenis tes psikologi — Kraepelin, tes koran, kepribadian, akademik, dan lainnya dalam satu aplikasi.</p>
        </div>

        {/* Produk & Harga — info publik, tidak perlu login untuk melihatnya */}
        <div className="mt-5 rounded-xl p-4 shrink-0" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
          <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--accent)' }}>Produk &amp; Harga</p>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Latihan Harian</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{totalModulDasar} modul psikotes, akses tak terbatas</p>
            </div>
            <span className="text-sm font-bold whitespace-nowrap" style={{ color: '#4ADE80' }}>Gratis</span>
          </div>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Tryout Simulasi Ujian</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Simulasi lengkap {totalModulTryoutAktif} modul + laporan hasil PDF</p>
            </div>
            <span className="text-sm font-bold whitespace-nowrap" style={{ color: 'var(--accent)' }}>Rp50.000 &ndash; Rp150.000</span>
          </div>
        </div>

        {/* Tab Login / Register */}
        <div className="flex mt-6 mb-5 rounded-xl overflow-hidden border border-[var(--border)] shrink-0">
          {['login', 'register'].map(m => (
            <button key={m} onClick={() => { setMode(m); setError(''); }}
              className="flex-1 py-2.5 text-sm font-semibold uppercase tracking-wide transition"
              style={{
                backgroundColor: mode === m ? 'var(--accent)' : 'var(--bg-card2)',
                color: mode === m ? 'var(--bg-app)' : 'var(--text-muted)',
                fontFamily: "'Poppins', sans-serif"
              }}>
              {m === 'login' ? 'Masuk' : 'Daftar'}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-xl text-xs" style={{ backgroundColor: '#3A2020', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' }}>
            {error}
          </div>
        )}

        <div className="space-y-3">
          {mode === 'register' && (
            <div>
              <label className="text-xs uppercase tracking-wider text-stone-500 mb-1.5 block">Username</label>
              <div className="flex items-center gap-2 bg-[var(--bg-card2)] border border-[var(--border)] rounded-lg px-3 py-3">
                <User size={16} className="text-stone-500" />
                <input type="text" placeholder="Nama tampilan Anda" value={nama}
                  onChange={e => setNama(e.target.value)}
                  className="bg-transparent outline-none text-[var(--text-primary)] text-sm placeholder-stone-600 w-full" />
              </div>
              <p className="text-xs text-stone-600 mt-1">Ini yang akan tampil sebagai nama Anda di aplikasi</p>
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-wider text-stone-500 mb-1.5 block">
              {mode === 'login' ? 'Email atau Username' : 'Email'}
            </label>
            <div className="flex items-center gap-2 bg-[var(--bg-card2)] border border-[var(--border)] rounded-lg px-3 py-3">
              <Mail size={16} className="text-stone-500" />
              <input type={mode === 'login' ? 'text' : 'email'}
                placeholder={mode === 'login' ? 'Email atau username Anda' : 'nama@email.com'} value={email}
                onChange={e => setEmail(e.target.value)}
                className="bg-transparent outline-none text-[var(--text-primary)] text-sm placeholder-stone-600 w-full" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-stone-500 mb-1.5 block">Kata Sandi</label>
            <div className="flex items-center gap-2 bg-[var(--bg-card2)] border border-[var(--border)] rounded-lg px-3 py-3">
              <Lock size={16} className="text-stone-500" />
              <input type="password" placeholder="••••••••" value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (mode === 'login' ? handleLogin() : handleRegister())}
                className="bg-transparent outline-none text-[var(--text-primary)] text-sm placeholder-stone-600 w-full" />
            </div>
          </div>
          {mode === 'register' && (
            <div>
              <label className="text-xs uppercase tracking-wider text-stone-500 mb-1.5 block">Kode Akses</label>
              <div className="flex items-center gap-2 bg-[var(--bg-card2)] border border-[var(--accent)]/40 rounded-lg px-3 py-3">
                <span className="text-stone-500 text-sm">🔑</span>
                <input type="text" placeholder="Masukkan kode akses" value={kodeAkses}
                  onChange={e => setKodeAkses(e.target.value.toUpperCase())}
                  className="bg-transparent outline-none text-[var(--accent)] text-sm placeholder-stone-600 w-full font-mono tracking-widest" />
              </div>
              {kodeDariUrl ? (
                <p className="text-xs mt-1" style={{ color: 'var(--success)' }}>✓ Kode referral otomatis terisi dari link kamu</p>
              ) : (
                <p className="text-xs text-stone-600 mt-1">Kode akses didapat dari admin atau bimbel Anda</p>
              )}
            </div>
          )}
        </div>

        <button
          onClick={mode === 'login' ? handleLogin : handleRegister}
          disabled={loading}
          className="mt-6 w-full bg-[var(--accent)] text-[var(--bg-app)] font-semibold tracking-wide py-3.5 rounded-lg text-sm uppercase active:scale-[0.98] transition-transform"
          style={{ fontFamily: "'Poppins', sans-serif", opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Memproses...' : mode === 'login' ? 'Masuk →' : 'Daftar Sekarang →'}
        </button>

        {mode === 'login' && (
          <button onClick={() => { setLupaPassword(true); setError(''); }}
            className="mt-4 w-full text-sm py-2"
            style={{ color: 'var(--text-muted)' }}>
            Lupa Password? <span style={{ color: 'var(--accent)' }}>Klik di sini</span>
          </button>
        )}
        <p className="text-center text-stone-600 text-[11px] mt-6 pb-4">Aplikasi latihan mandiri — bukan media resmi instansi</p>
      </div>
    </div>
  );
}

function ModuleCard({ mod, onOpen, terkunci }) {
  const Icon = mod.icon;
  return (
    <button onClick={() => onOpen(mod)}
      className="w-full flex items-center gap-3 rounded-xl p-4 text-left active:scale-[0.99] transition-all"
      style={{
        backgroundColor: 'var(--bg-card2)',
        border: '1px solid var(--border)',
        opacity: terkunci ? 0.55 : 1,
      }}>
      <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
           style={{ backgroundColor: 'var(--bg-card3)', border: '1px solid var(--border2)' }}>
        {terkunci ? <Lock size={18} style={{ color: 'var(--text-muted)' }} /> : <Icon size={20} style={{ color: 'var(--accent)' }} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{mod.name}</p>
          {mod.beta && (
            <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full flex-shrink-0"
                  style={{ color: '#D97706', backgroundColor: '#FEF3C7' }}>Baru</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[11px] mb-1" style={{ color: 'var(--text-muted)' }}>
          <Clock size={11} />
          <span>{mod.duration}</span>
        </div>
        {terkunci ? (
          <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--danger)' }}>🔒 Terkunci — selesaikan tryout</span>
        ) : (
          <StatusBadge status={mod.status} score={mod.score} />
        )}
      </div>
      <ChevronRight size={18} style={{ color: 'var(--text-faint)' }} className="flex-shrink-0" />
    </button>
  );
}

function BerandaScreen({ rankInfo, onOpenModule, tryoutAktif, tryoutSudah, tryoutWajib, latihanTerkunci, tryoutPerluBayar, tryoutSudahBayar, onBukaTryout, onLihatProfilLengkap }) {
  const recommended = MODULES.filter((m) => m.status !== 'selesai').slice(0, 2);
  const userData = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const namaUser = userData?.nama || 'Pengguna';
  const sapaanWaktu = () => {
    const jam = new Date().getHours();
    if (jam < 11) return 'Selamat pagi';
    if (jam < 15) return 'Selamat siang';
    if (jam < 18) return 'Selamat sore';
    return 'Selamat malam';
  };

  // Statistik real dari localStorage
  const riwayat = (() => { try { return JSON.parse(localStorage.getItem('riwayatLatihan')) || []; } catch { return []; } })();
  const totalSesi = riwayat.length;
  const rataRata = totalSesi > 0 ? Math.round(riwayat.reduce((a, b) => a + b.skor, 0) / totalSesi) : 0;

  // Hitung streak latihan harian
  const hitungStreak = () => {
    if (riwayat.length === 0) return 0;
    const hariUnik = [...new Set(riwayat.map(r => new Date(r.tanggal).toDateString()))].sort((a, b) => new Date(b) - new Date(a));
    if (hariUnik.length === 0) return 0;
    let streak = 0;
    const hari_ini = new Date().toDateString();
    const kemarin = new Date(Date.now() - 86400000).toDateString();
    // Mulai hitung jika latihan hari ini atau kemarin
    if (hariUnik[0] !== hari_ini && hariUnik[0] !== kemarin) return 0;
    for (let i = 0; i < hariUnik.length; i++) {
      const selisih = (new Date(hariUnik[0]) - new Date(hariUnik[i])) / 86400000;
      if (selisih === i) streak++;
      else break;
    }
    return streak;
  };
  const streak = hitungStreak();

  return (
    <div className="px-5 pt-6 pb-4 space-y-4" style={{ backgroundColor: 'var(--bg-app)' }}>
      {/* Header dengan streak */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{sapaanWaktu()},</p>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>
            {namaUser} 👋
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {streak > 0 && (
            <div className="flex flex-col items-center px-3 py-2 rounded-xl"
                 style={{ backgroundColor: streak >= 7 ? 'rgba(251,191,36,0.15)' : 'rgba(16,185,129,0.12)', border: `1px solid ${streak >= 7 ? 'rgba(251,191,36,0.4)' : 'var(--border2)'}` }}>
              <span className="text-2xl">{streak >= 7 ? '🔥' : '⚡'}</span>
              <span className="text-lg font-extrabold font-mono leading-none" style={{ color: streak >= 7 ? 'var(--warning)' : 'var(--accent)' }}>{streak}</span>
              <span className="text-[9px] uppercase tracking-wide" style={{ color: streak >= 7 ? 'var(--warning)' : 'var(--text-muted)' }}>hari</span>
            </div>
          )}
          <ProfilShortcut onLihatProfilLengkap={onLihatProfilLengkap} />
        </div>
      </div>

      {/* Motivasi streak */}
      {streak > 0 && (
        <div className="rounded-xl px-4 py-2.5 flex items-center gap-3"
             style={{ backgroundColor: streak >= 7 ? 'rgba(251,191,36,0.1)' : 'rgba(16,185,129,0.08)', border: `1px solid ${streak >= 7 ? 'rgba(251,191,36,0.3)' : 'var(--border)'}` }}>
          <span className="text-lg">{streak >= 7 ? '🔥' : streak >= 3 ? '⚡' : '✨'}</span>
          <p className="text-xs font-medium" style={{ color: streak >= 7 ? 'var(--warning)' : 'var(--accent)' }}>
            {streak >= 7 ? `${streak} hari streak! Kamu konsisten banget!` :
             streak >= 3 ? `${streak} hari berturut-turut! Terus pertahankan!` :
             `${streak} hari latihan. Ayo jaga streaknya!`}
          </p>
        </div>
      )}

      <InfoRekrutmenBanner />
      <GamifikasiCard />

      {/* Banner Tryout Aktif — kalau belum selesai, ini WAJIB dan mengunci latihan harian */}
      {tryoutWajib && (
        <button onClick={onBukaTryout} className="w-full text-left rounded-xl p-4 flex items-center gap-3"
          style={{ backgroundColor: 'rgba(220,38,38,0.1)', border: '1.5px solid var(--danger)' }}>
          <span className="text-2xl">🔒</span>
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--danger)' }}>Tryout Wajib Diselesaikan</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{tryoutAktif.judul}</p>
            {tryoutPerluBayar && !tryoutSudahBayar && tryoutAktif.harga > 0 && (
              <p className="text-sm font-bold mt-0.5" style={{ color: 'var(--accent)' }}>
                Rp{Number(tryoutAktif.harga).toLocaleString('id-ID')}
              </p>
            )}
            <p className="text-[11px] mt-0.5 leading-snug" style={{ color: 'var(--text-muted)' }}>
              {!latihanTerkunci
                ? 'Latihan harian sudah dibukakan admin untukmu. Tryout ini tetap perlu kamu selesaikan.'
                : tryoutPerluBayar && !tryoutSudahBayar
                ? 'Latihan harian terkunci sementara. Bayar lalu kerjakan tryout ini untuk membukanya kembali.'
                : 'Latihan harian terkunci sementara. Kerjakan tryout ini untuk membukanya kembali.'}
            </p>
          </div>
          <ChevronRight size={18} style={{ color: 'var(--danger)' }} />
        </button>
      )}
      {tryoutAktif && tryoutSudah && (
        <button onClick={onBukaTryout} className="w-full text-left rounded-xl p-4 flex items-center gap-3"
          style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--success)' }}>
          <span className="text-2xl">✅</span>
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--success)' }}>Tryout selesai</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Lihat hasil {tryoutAktif.judul}</p>
          </div>
          <ChevronRight size={18} style={{ color: 'var(--success)' }} />
        </button>
      )}

      <ProgressBeranda rankInfo={rankInfo} />

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: 'var(--text-primary)' }}>Lanjutkan Latihan</h3>
          {latihanTerkunci ? <Lock size={14} style={{ color: 'var(--danger)' }} /> : <Star size={14} style={{ color: 'var(--accent)' }} />}
        </div>
        <div className="space-y-3">
          {recommended.map((m) => (<ModuleCard key={m.id} mod={m} onOpen={onOpenModule} terkunci={latihanTerkunci} />))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl p-3 text-center" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
          <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Rata-rata</p>
          <p className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'JetBrains Mono', monospace" }}>
            {totalSesi > 0 ? rataRata : '—'}
          </p>
        </div>
        <div className="rounded-xl p-3 text-center" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
          <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Sesi</p>
          <p className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'JetBrains Mono', monospace" }}>
            {totalSesi}
          </p>
        </div>
        <div className="rounded-xl p-3 text-center" style={{ backgroundColor: streak > 0 ? 'rgba(16,185,129,0.1)' : 'var(--bg-card2)', border: `1px solid ${streak > 0 ? 'var(--border2)' : 'var(--border)'}` }}>
          <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Streak</p>
          <p className="text-xl font-bold" style={{ color: streak > 0 ? 'var(--accent)' : 'var(--text-primary)', fontFamily: "'JetBrains Mono', monospace" }}>
            {streak > 0 ? `${streak}🔥` : '—'}
          </p>
        </div>
      </div>
    </div>
  );
}

function ModulScreen({ onOpenModule, tryoutWajib, latihanTerkunci, tryoutPerluBayar, tryoutSudahBayar, onBukaTryout }) {
  return (
    <div className="px-5 pt-6 pb-4 space-y-4" style={{ backgroundColor: 'var(--bg-app)' }}>
      <div>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Pilih modul untuk latihan</p>
        <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>Semua Modul</h2>
      </div>

      {tryoutWajib && (
        <button onClick={onBukaTryout} className="w-full text-left rounded-xl p-4 flex items-center gap-3"
          style={{ backgroundColor: 'rgba(220,38,38,0.1)', border: '1.5px solid var(--danger)' }}>
          <span className="text-2xl">🔒</span>
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--danger)' }}>Tryout Wajib Diselesaikan</p>
            <p className="text-[11px] mt-0.5 leading-snug" style={{ color: 'var(--text-muted)' }}>
              {!latihanTerkunci
                ? 'Modul latihan sudah dibukakan admin untukmu. Tryout ini tetap perlu kamu selesaikan.'
                : tryoutPerluBayar && !tryoutSudahBayar
                ? 'Semua modul di bawah terkunci. Bayar lalu kerjakan tryout untuk membukanya.'
                : 'Semua modul di bawah terkunci. Kerjakan tryout untuk membukanya.'}
            </p>
          </div>
          <ChevronRight size={18} style={{ color: 'var(--danger)' }} />
        </button>
      )}

      <div className="space-y-3">
        {MODULES.map((m) => (<ModuleCard key={m.id} mod={m} onOpen={onOpenModule} terkunci={latihanTerkunci} />))}
      </div>
    </div>
  );
}

function ModuleDetailScreen({ mod, onBack }) {
  const Icon = mod.icon;
  const { jalur } = useJalur();
  const deskripsiJalur = getDeskripsiModul(mod.id, jalur);
  const desc = deskripsiJalur ? deskripsiJalur.desc : mod.desc;
  const features = deskripsiJalur ? deskripsiJalur.features : mod.features;

  return (
    <div className="px-5 pt-6 pb-4 space-y-5">
      <button onClick={onBack} className="flex items-center gap-2 text-stone-400 text-sm"><ArrowLeft size={16} /> Kembali</button>
      <div className="flex items-start gap-3">
        <div className="w-14 h-14 rounded-xl bg-[var(--bg-card2)] border border-[var(--border)] flex items-center justify-center flex-shrink-0">
          <Icon size={26} className="text-[var(--accent)]" />
        </div>
        <div>
          <h2 className="text-[var(--text-primary)] text-xl leading-tight" style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>{mod.name}</h2>
          <p className="text-stone-500 text-xs mt-1 flex items-center gap-1"><Clock size={12} /> {mod.duration}</p>
        </div>
      </div>
      <span className={`inline-block text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide
        ${jalur === 'bintara' ? 'bg-amber-900/40 text-amber-300 border border-amber-700/40' : 'bg-green-900/40 text-green-300 border border-green-700/40'}`}>
        {jalur === 'bintara' ? '⭐ Jalur Bintara' : '🛡️ Jalur Tamtama'}
      </span>
      <p className="text-stone-300 text-sm leading-relaxed">{desc}</p>
      <StatusBadge status={mod.status} score={mod.score} />
      <div className="bg-[var(--bg-card2)] border border-[var(--border)] rounded-xl p-4">
        <p className="text-[var(--accent)] text-xs font-semibold uppercase tracking-wider mb-3">Fitur yang akan tersedia</p>
        <ul className="space-y-2.5">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-stone-300 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] mt-1.5 flex-shrink-0" />{f}
            </li>
          ))}
        </ul>
      </div>
      <div className="bg-[var(--bg-card3)]/60 border border-[var(--border)] rounded-xl p-4">
        <p className="text-stone-400 text-xs leading-relaxed">Modul ini masih dalam tahap pengembangan.</p>
      </div>
      <button disabled className="w-full bg-[var(--border)] text-stone-500 font-semibold tracking-wide py-3.5 rounded-lg text-sm uppercase cursor-not-allowed" style={{ fontFamily: "'Poppins', sans-serif" }}>Segera Hadir</button>
    </div>
  );
}

function RiwayatScreen() {
  const [tab, setTab] = useState('riwayat'); // riwayat | analisis
  const [loadingPdfHarian, setLoadingPdfHarian] = useState(false);
  const [errorPdfHarian, setErrorPdfHarian] = useState('');

  const handleUnduhPdfHarian = async () => {
    setLoadingPdfHarian(true);
    setErrorPdfHarian('');
    try {
      const token = localStorage.getItem('token');
      await unduhPdf(`${API_URL_LAPORAN}/api/laporan/harian`, token, 'laporan-latihan.pdf');
    } catch (err) {
      setErrorPdfHarian(err.message || 'Gagal mengunduh laporan.');
    }
    setLoadingPdfHarian(false);
  };

  // Ambil data dari localStorage
  const riwayat = (() => { try { return JSON.parse(localStorage.getItem('riwayatLatihan')) || []; } catch { return []; } })();

  // Urutkan dari terbaru
  const riwayatUrut = [...riwayat].sort((a, b) => new Date(b.tanggal) - new Date(a.tanggal));

  // Analisis kelemahan — hitung rata-rata skor per modul
  const analisisModul = {};
  riwayat.forEach(r => {
    if (!analisisModul[r.modulId]) analisisModul[r.modulId] = { total: 0, count: 0, skor_list: [] };
    analisisModul[r.modulId].total += r.skor;
    analisisModul[r.modulId].count += 1;
    analisisModul[r.modulId].skor_list.push(r.skor);
  });

  const modulAnalisis = Object.entries(analisisModul).map(([id, data]) => ({
    id,
    rata: Math.round(data.total / data.count),
    count: data.count,
    tren: data.skor_list.length >= 2
      ? data.skor_list[data.skor_list.length - 1] - data.skor_list[data.skor_list.length - 2]
      : 0,
  })).sort((a, b) => a.rata - b.rata); // urutkan dari terlemah

  const MODUL_LABEL = {
    kraepelin: { nama: 'Kraepelin/Pauli', icon: '🧮' },
    kecermatan: { nama: 'Kecermatan', icon: '✅' },
    logika: { nama: 'Logika & Numerik', icon: '🔢' },
    verbal: { nama: 'Verbal', icon: '📖' },
    figural: { nama: 'Figural & Spasial', icon: '🧩' },
    kepribadian: { nama: 'Kepribadian', icon: '🧠' },
    akademik: { nama: 'Kemampuan Akademik', icon: '📚' },
    armyalpha: { nama: 'Instruksi Cepat', icon: '⚡' },
    mekanik: { nama: 'Tes Bakat Mekanik', icon: '⚙️' },
    substitusi: { nama: 'Tes Substitusi Simbol', icon: '🔣' },
    kestabilan: { nama: 'Kestabilan Pribadi', icon: '🛡️' },
    interpretasigambar: { nama: 'Interpretasi Gambar', icon: '🖼️' },
    interpretasibentuk: { nama: 'Interpretasi Bentuk', icon: '🔮' },
  };

  // Grafik tren 7 sesi terakhir
  const tren7 = riwayatUrut.slice(0, 7).reverse();
  const W = 300, H = 80, PAD = 10;
  const maks = Math.max(100, ...tren7.map(d => d.skor));
  const points = tren7.length >= 2
    ? tren7.map((d, i) => {
        const x = PAD + (i / (tren7.length - 1)) * (W - PAD * 2);
        const y = H - PAD - (d.skor / maks) * (H - PAD * 2);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      }).join(' ')
    : null;

  const formatTgl = (iso) => {
    const d = new Date(iso);
    return `${d.getDate()} ${['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agu','Sep','Okt','Nov','Des'][d.getMonth()]} · ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`;
  };

  const getBadge = (skor) => {
    if (skor >= 85) return { label: 'Luar Biasa', color: 'var(--success)' };
    if (skor >= 70) return { label: 'Baik', color: 'var(--accent)' };
    if (skor >= 55) return { label: 'Cukup', color: 'var(--warning)' };
    return { label: 'Perlu Latihan', color: 'var(--danger)' };
  };

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: 'var(--bg-app)' }}>
      {/* Header */}
      <div className="px-5 pt-6 pb-3 flex-shrink-0 flex items-start justify-between gap-3">
        <div>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Jejak latihan kamu</p>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>Riwayat</h2>
        </div>
        <button onClick={handleUnduhPdfHarian} disabled={loadingPdfHarian}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold shrink-0"
          style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--accent)', color: 'var(--accent)' }}>
          <Download size={14} /> {loadingPdfHarian ? '...' : 'PDF'}
        </button>
      </div>
      {errorPdfHarian && <p className="px-5 text-xs" style={{ color: '#f87171' }}>{errorPdfHarian}</p>}

      {/* Tab */}
      <div className="flex px-5 gap-2 mb-3 flex-shrink-0">
        {[
          { id: 'riwayat', label: '📋 Riwayat' },
          { id: 'analisis', label: '📊 Analisis' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className="flex-1 py-2 rounded-xl text-xs font-semibold transition"
            style={{
              backgroundColor: tab === t.id ? 'var(--accent)' : 'var(--bg-card2)',
              color: tab === t.id ? '#fff' : 'var(--text-muted)',
              border: '1px solid var(--border)',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-4 min-h-0">

        {/* ── Tab Riwayat ── */}
        {tab === 'riwayat' && (
          <div className="space-y-3">
            {/* Grafik tren */}
            {tren7.length >= 2 && (
              <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
                <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--accent)' }}>
                  Tren 7 Sesi Terakhir
                </p>
                <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 80 }}>
                  <polyline points={points} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  {tren7.map((d, i) => {
                    const x = PAD + (i / (tren7.length - 1)) * (W - PAD * 2);
                    const y = H - PAD - (d.skor / maks) * (H - PAD * 2);
                    return (
                      <g key={i}>
                        <circle cx={x.toFixed(1)} cy={y.toFixed(1)} r="4" fill="var(--accent)"/>
                        <text x={x.toFixed(1)} y={(y - 8).toFixed(1)} textAnchor="middle" fontSize="9" fill="var(--text-muted)">{d.skor}</text>
                      </g>
                    );
                  })}
                </svg>
              </div>
            )}

            {/* List riwayat */}
            {riwayatUrut.length === 0 ? (
              <div className="text-center py-12 rounded-xl" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
                <p className="text-3xl mb-2">📋</p>
                <p style={{ color: 'var(--text-muted)' }} className="text-sm">Belum ada riwayat latihan.</p>
                <p style={{ color: 'var(--text-faint)' }} className="text-xs mt-1">Kerjakan modul pertamamu!</p>
              </div>
            ) : riwayatUrut.map((h, i) => {
              const info = MODUL_LABEL[h.modulId] || { nama: h.modulId, icon: '📝' };
              const badge = getBadge(h.skor);
              return (
                <div key={i} className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{info.icon}</span>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{info.nama}</p>
                        <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{formatTgl(h.tanggal)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold font-mono" style={{ color: 'var(--accent)' }}>{h.skor}</p>
                      <p className="text-[10px] font-semibold" style={{ color: badge.color }}>{badge.label}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Tab Analisis Kelemahan ── */}
        {tab === 'analisis' && (
          <div className="space-y-3">
            <div className="rounded-xl p-3" style={{ backgroundColor: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)' }}>
              <p className="text-xs" style={{ color: 'var(--danger)' }}>
                💡 Modul dengan skor terendah perlu latihan lebih sering
              </p>
            </div>

            {modulAnalisis.length === 0 ? (
              <div className="text-center py-12 rounded-xl" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
                <p className="text-3xl mb-2">📊</p>
                <p style={{ color: 'var(--text-muted)' }} className="text-sm">Belum ada data analisis.</p>
                <p style={{ color: 'var(--text-faint)' }} className="text-xs mt-1">Kerjakan beberapa modul dulu!</p>
              </div>
            ) : modulAnalisis.map((m, i) => {
              const info = MODUL_LABEL[m.id] || { nama: m.id, icon: '📝' };
              const isLemah = m.rata < 60;
              return (
                <div key={m.id} className="rounded-xl p-4" style={{
                  backgroundColor: 'var(--bg-card2)',
                  border: `1px solid ${isLemah ? 'rgba(220,38,38,0.3)' : 'var(--border)'}`,
                }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span>{info.icon}</span>
                      <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{info.nama}</p>
                      {isLemah && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(220,38,38,0.1)', color: 'var(--danger)' }}>Perlu latihan</span>}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold font-mono" style={{ color: isLemah ? 'var(--danger)' : 'var(--accent)' }}>{m.rata}</p>
                      <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{m.count}x latihan</p>
                    </div>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--bg-card3)' }}>
                    <div className="h-full rounded-full transition-all"
                         style={{ width: `${m.rata}%`, backgroundColor: isLemah ? 'var(--danger)' : 'var(--accent)' }} />
                  </div>
                  {m.tren !== 0 && (
                    <p className="text-[10px] mt-1" style={{ color: m.tren > 0 ? 'var(--success)' : 'var(--danger)' }}>
                      {m.tren > 0 ? `📈 Naik ${m.tren} poin dari sesi sebelumnya` : `📉 Turun ${Math.abs(m.tren)} poin dari sesi sebelumnya`}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfilScreen({ rankInfo, onLogout, onGantiJalur }) {
  const { jalur } = useJalur();
  const [subMenu, setSubMenu] = useState(null);
  const [nama, setNama]       = useState('');
  const [loading, setLoading] = useState(false);
  const [pesan, setPesan]     = useState('');

  const userData = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const namaUser  = userData?.nama  || 'Pengguna';
  const emailUser = userData?.email || '-';

  // Set nama awal saat subMenu edit profil dibuka
  const handleBukaEditProfil = () => {
    setNama(namaUser);
    setPesan('');
    setSubMenu('editProfil');
  };

  const handleSimpan = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/profil/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ nama }),
      });
      const data = await res.json();
      if (data.user) {
        localStorage.setItem('user', JSON.stringify({ ...userData, nama: data.user.nama }));
        setPesan('Profil berhasil diperbarui!');
      } else { setPesan(data.error || 'Gagal menyimpan'); }
    } catch { setPesan('Tidak bisa terhubung ke server'); }
    setLoading(false);
  };

  if (subMenu === 'editProfil') {
    return (
      <div className="px-5 pt-6 pb-4 space-y-5">
        <button onClick={() => setSubMenu(null)} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
          <ArrowLeft size={16} /> Kembali
        </button>
        <div>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Akun kamu</p>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>Edit Profil</h2>
        </div>

        <div className="rounded-xl p-5 space-y-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
          <div>
            <label className="text-xs uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Nama Lengkap</label>
            <input type="text" value={nama} onChange={e => setNama(e.target.value)}
              className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-card3)', color: 'var(--text-primary)', border: '1px solid var(--border)' }} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Email</label>
            <div className="px-4 py-3 rounded-xl text-sm" style={{ backgroundColor: 'var(--bg-card3)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
              {emailUser} <span className="text-xs">(tidak bisa diubah)</span>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Jalur Seleksi</label>
            <div className="px-4 py-3 rounded-xl text-sm flex items-center justify-between"
                 style={{ backgroundColor: 'var(--bg-card3)', border: '1px solid var(--border)' }}>
              <span style={{ color: jalur === 'bintara' ? '#FBBF24' : '#4ADE80' }}>
                {jalur === 'bintara' ? '⭐ Bintara' : '🛡️ Tamtama'}
              </span>
              <button onClick={onGantiJalur} className="text-xs px-2 py-1 rounded-lg" style={{ backgroundColor: 'var(--bg-card2)', color: 'var(--accent)', border: '1px solid var(--border)' }}>
                Ganti
              </button>
            </div>
          </div>

          {pesan && (
            <div className="px-4 py-2 rounded-xl text-xs text-center"
                 style={{ backgroundColor: pesan.includes('berhasil') ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)', color: pesan.includes('berhasil') ? 'var(--success)' : 'var(--danger)', border: `1px solid ${pesan.includes('berhasil') ? 'var(--success)' : 'var(--danger)'}` }}>
              {pesan}
            </div>
          )}

          <button onClick={handleSimpan} disabled={loading}
            className="w-full py-3 rounded-xl font-bold text-sm uppercase tracking-wide"
            style={{ backgroundColor: 'var(--accent)', color: 'var(--bg-app)', fontFamily: "'Poppins', sans-serif", opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Menyimpan...' : 'Simpan Perubahan'}
          </button>
        </div>
      </div>
    );
  }

  // ── Notifikasi ────────────────────────────────────────────────
  if (subMenu === 'notifikasi') {
    return (
      <div className="flex flex-col h-full" style={{ backgroundColor: 'var(--bg-app)' }}>
        <div className="px-5 pt-6 pb-4 flex-shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setSubMenu(null)} className="flex items-center gap-2 text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
            <ArrowLeft size={16} /> Kembali
          </button>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Informasi & Pengumuman</p>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>Notifikasi</h2>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {INFO_REKRUTMEN.map(n => (
            <div key={n.id} className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                     style={{ backgroundColor: `${n.warna}20`, border: `1px solid ${n.warna}40` }}>
                  <span className="text-lg">{n.icon}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-bold leading-tight" style={{ color: 'var(--text-primary)' }}>{n.judul}</p>
                    <span className="text-[10px] flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{n.waktu}</span>
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>{n.isi}</p>
                </div>
              </div>
            </div>
          ))}
          <p className="text-center text-xs py-2" style={{ color: 'var(--text-faint)' }}>
            Informasi bersumber dari ad.rekrutmen-tni.mil.id (resmi)
          </p>
        </div>
      </div>
    );
  }

  // ── Tentang Aplikasi ──────────────────────────────────────────
  if (subMenu === 'tentang') {
    return (
      <div className="flex flex-col h-full overflow-y-auto" style={{ backgroundColor: 'var(--bg-app)' }}>
        <div className="px-5 pt-6 pb-4 flex-shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setSubMenu(null)} className="flex items-center gap-2 text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
            <ArrowLeft size={16} /> Kembali
          </button>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Informasi Aplikasi</p>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>Tentang Aplikasi</h2>
        </div>

        <div className="px-5 py-6 space-y-5">
          {/* Logo & versi */}
          <div className="rounded-2xl p-6 text-center" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'var(--accent)' }}>
              <span className="text-3xl">🎯</span>
            </div>
            <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>
              SKORTES
            </h3>
            <p className="text-xs mt-1 mb-3" style={{ color: 'var(--text-muted)' }}>Aplikasi Latihan Simulasi Psikotes</p>
            <div className="flex items-center justify-center gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: 'var(--bg-card3)', color: 'var(--accent)', border: '1px solid var(--border)' }}>
                Versi 3.0.0
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: 'rgba(74,222,128,0.1)', color: 'var(--success)', border: '1px solid rgba(74,222,128,0.3)' }}>
                Stable
              </span>
            </div>
          </div>

          {/* Info teknis */}
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
            <div className="px-4 py-2" style={{ backgroundColor: 'var(--bg-card2)', borderBottom: '1px solid var(--border)' }}>
              <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--accent)' }}>Informasi Teknis</p>
            </div>
            {[
              { label: 'Versi Aplikasi', value: '3.0.0' },
              { label: 'Tanggal Rilis', value: 'Juli 2026' },
              { label: 'Platform', value: 'Web (React)' },
              { label: 'Total Modul', value: '11 Modul Psikotes' },
              { label: 'Bank Soal', value: '1000+ Soal' },
              { label: 'Server', value: 'Jakarta, Indonesia' },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3"
                   style={{ backgroundColor: i % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-card2)', borderBottom: '1px solid var(--border)' }}>
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>{item.label}</span>
                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{item.value}</span>
              </div>
            ))}
          </div>

          {/* Modul tersedia */}
          <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
            <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--accent)' }}>Modul Tersedia</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { nama: 'Kraepelin/Pauli', icon: '🧮' },
                { nama: 'Kecermatan NNC-2', icon: '✅' },
                { nama: 'Logika & Numerik', icon: '🔢' },
                { nama: 'Verbal', icon: '📖' },
                { nama: 'Figural & Spasial', icon: '🧩' },
                { nama: 'Kepribadian', icon: '🧠' },
                { nama: 'Instruksi Cepat', icon: '⚡' },
                { nama: 'Kemampuan Akademik', icon: '📚' },
                { nama: 'Bakat Mekanik', icon: '⚙️' },
                { nama: 'Substitusi Simbol', icon: '🔣' },
                { nama: 'Kestabilan Pribadi', icon: '🛡️' },
                { nama: 'Interpretasi Gambar', icon: '🖼️' },
                { nama: 'Interpretasi Bentuk', icon: '🔮' },
              ].map((m, i) => (
                <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg" style={{ backgroundColor: 'var(--bg-card3)' }}>
                  <span className="text-sm">{m.icon}</span>
                  <span className="text-xs" style={{ color: 'var(--text-primary)' }}>{m.nama}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
            <p className="text-xs font-bold mb-2" style={{ color: 'var(--accent)' }}>⚠️ Disclaimer</p>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
              Aplikasi ini merupakan alat latihan mandiri dan tidak berafiliasi dengan instansi manapun secara resmi.
              Seluruh soal dan materi disusun berdasarkan referensi publik untuk tujuan pembelajaran.
              Hasil latihan tidak menjamin kelulusan seleksi sesungguhnya.
            </p>
          </div>

          <p className="text-center text-xs" style={{ color: 'var(--text-faint)' }}>
            © 2026 Skortes. All rights reserved.
          </p>
        </div>
      </div>
    );
  }

  // ── Menu Utama Profil ─────────────────────────────────────────
  return (
    <div className="px-5 pt-6 pb-4 space-y-5 overflow-y-auto" style={{ backgroundColor: 'var(--bg-app)' }}>
      <div>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Akun kamu</p>
        <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>Profil</h2>
      </div>

      {/* Kartu user */}
      <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
               style={{ backgroundColor: 'var(--bg-card3)', border: '2px solid var(--accent)' }}>
            <span className="text-2xl font-bold" style={{ color: 'var(--accent)', fontFamily: "'Poppins', sans-serif" }}>
              {namaUser.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-lg font-bold truncate" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>
              {namaUser}
            </p>
            <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{emailUser}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--accent)' }}>
              {jalur === 'bintara' ? '⭐ Calon Bintara' : '🛡️ Calon Tamtama'}
            </p>
          </div>
        </div>
        <div className="h-px mb-4" style={{ backgroundColor: 'var(--border)' }} />
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'JetBrains Mono', monospace" }}>
              {rankInfo.done}/{rankInfo.total}
            </p>
            <p className="text-[10px] uppercase tracking-wide mt-0.5" style={{ color: 'var(--text-muted)' }}>Modul Selesai</p>
          </div>
          <div>
            <p className="text-lg font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'JetBrains Mono', monospace" }}>78.5</p>
            <p className="text-[10px] uppercase tracking-wide mt-0.5" style={{ color: 'var(--text-muted)' }}>Rata-rata</p>
          </div>
          <div>
            <p className="text-lg font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'JetBrains Mono', monospace" }}>1.9j</p>
            <p className="text-[10px] uppercase tracking-wide mt-0.5" style={{ color: 'var(--text-muted)' }}>Total Waktu</p>
          </div>
        </div>
      </div>

      {/* Jalur aktif */}
      <button onClick={onGantiJalur} className="w-full text-left rounded-xl p-4"
              style={{ border: `1px solid ${jalur === 'bintara' ? 'rgba(251,191,36,0.4)' : 'rgba(74,222,128,0.4)'}`, backgroundColor: jalur === 'bintara' ? 'rgba(251,191,36,0.08)' : 'rgba(74,222,128,0.08)' }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Jalur Latihan Aktif</p>
            <p className="font-bold text-sm" style={{ color: jalur === 'bintara' ? '#FBBF24' : '#4ADE80' }}>
              {jalur === 'bintara' ? '⭐ Calon Bintara' : '🛡️ Calon Tamtama'}
            </p>
          </div>
          <span className="text-xs flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>Ganti <ChevronRight size={14} /></span>
        </div>
      </button>

      {/* Menu */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
        <ToggleTema />
        <TombolPushNotif />
        {[
          { id: 'editProfil', label: 'Edit Profil', icon: '✏️', desc: 'Ubah nama dan informasi akun', action: handleBukaEditProfil },
          { id: 'notifikasi', label: 'Notifikasi', icon: '🔔', desc: 'Info tryout & jadwal rekrutmen', action: () => setSubMenu('notifikasi') },
          { id: 'tentang', label: 'Tentang Aplikasi', icon: 'ℹ️', desc: 'Versi 3.0.0 • Info aplikasi', action: () => setSubMenu('tentang') },
        ].map((item, i) => (
          <button key={item.id} onClick={item.action}
            className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
            style={{ borderTop: '1px solid var(--border)' }}>
            <span className="text-lg">{item.icon}</span>
            <div className="flex-1">
              <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{item.label}</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.desc}</p>
            </div>
            <ChevronRight size={16} style={{ color: 'var(--text-faint)' }} />
          </button>
        ))}
      </div>

      <button onClick={onLogout}
        className="w-full flex items-center justify-center gap-2 text-sm font-semibold py-3 rounded-xl"
        style={{ color: 'var(--danger)', border: '1px solid rgba(248,113,113,0.3)', backgroundColor: 'rgba(248,113,113,0.05)' }}>
        <LogOut size={16} /> Keluar dari Akun
      </button>
    </div>
  );
}

function BottomNav({ active, onChange }) {
  const items = [
    { id: 'beranda', label: 'Beranda', icon: Home },
    { id: 'modul', label: 'Modul', icon: LayoutGrid },
    { id: 'riwayat', label: 'Riwayat', icon: History },
    { id: 'profil', label: 'Profil', icon: User },
  ];
  return (
    <div className="flex items-center justify-around py-2 px-2 flex-shrink-0"
         style={{ backgroundColor: 'var(--bg-card)', borderTop: '1px solid var(--border)' }}>
      {items.map((item) => {
        const Icon = item.icon;
        const isActive = active === item.id;
        return (
          <button key={item.id} onClick={() => onChange(item.id)}
            className="flex flex-col items-center gap-1 py-1 px-4 rounded-xl transition-all"
            style={{ backgroundColor: isActive ? 'rgba(16,185,129,0.12)' : 'transparent' }}>
            <Icon size={20} strokeWidth={isActive ? 2.5 : 1.8}
              style={{ color: isActive ? 'var(--accent)' : 'var(--text-muted)' }} />
            <span className="text-[10px] font-medium"
              style={{ color: isActive ? 'var(--accent)' : 'var(--text-muted)' }}>
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function AppContent() {
  const [loggedIn, setLoggedIn] = useState(() => !!localStorage.getItem('token'));
  const [, setUserData] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });
  const [tab, setTab] = useState('beranda');
  const [selectedModule, setSelectedModule] = useState(null);
  const [gantiJalurMode, setGantiJalurMode] = useState(false);
  const { jalur, setJalur } = useJalur();

  // ── Tryout resmi ────────────────────────────────────────────
  const [tryoutAktif, setTryoutAktif] = useState(null);       // data tryout yang sedang berjalan
  const [tryoutSudah, setTryoutSudah] = useState(false);       // user sudah pernah submit tryout ini
  const [tryoutHasil, setTryoutHasil] = useState(null);        // hasil kalau sudah submit
  const [showTryoutScreen, setShowTryoutScreen] = useState(false);
  const [tryoutModulSelesai, setTryoutModulSelesai] = useState({}); // {modulId: skor} progress tryout berjalan
  const [tryoutModeModulId, setTryoutModeModulId] = useState(null); // modul yg lagi dikerjakan DALAM konteks tryout
  // Jam mulai percobaan modul tryout saat ini — dipakai buat memastikan sesi_latihan
  // yang diambil di handleBack() BENAR-BENAR hasil percobaan ini, bukan sesi lama
  // yang kebetulan tersimpan sebelumnya (lihat catatan panjang di handleBack).
  const tryoutModulMulaiAtRef = useRef(null);
  const [loadingSubmitTryout, setLoadingSubmitTryout] = useState(false);
  const [tryoutPerluBayar, setTryoutPerluBayar] = useState(false);
  const [tryoutSudahBayar, setTryoutSudahBayar] = useState(true);
  // PENTING: status bayar di atas cuma default AWAL sebelum status asli dari
  // server datang (defaultnya "tidak perlu bayar" biar tidak nge-flash paywall
  // ke tryout gratis saat baru render). Selama status asli belum datang, jangan
  // pernah anggap modul "boleh dikerjakan" — makanya butuh flag terpisah ini,
  // supaya window loading di awal tidak jadi celah user bisa mulai modul
  // berbayar sebelum status bayar yang sebenarnya sempat dicek.
  const [statusBayarTryoutSiap, setStatusBayarTryoutSiap] = useState(false);
  const [izinLatihanHarian, setIzinLatihanHarian] = useState(false); // dibukakan admin per user
  const [loadingBayarTryout, setLoadingBayarTryout] = useState(false);
  const [overlayTryoutDitutup, setOverlayTryoutDitutup] = useState(false); // alert full-screen ditutup sementara

  const muatStatusTryout = () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/api/tryouts/aktif`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        if (data.tryout) {
          setTryoutAktif(data.tryout);
          setTryoutSudah(data.sudahDikerjakan);
          setTryoutHasil(data.hasil);
          setTryoutPerluBayar(!!data.perluBayar);
          setTryoutSudahBayar(!!data.sudahBayar);
          setIzinLatihanHarian(!!data.izinLatihanHarian);
          setStatusBayarTryoutSiap(true);
          if (!data.sudahDikerjakan) {
            try {
              const simpanan = JSON.parse(localStorage.getItem(`tryoutProgress_${data.tryout.id}`)) || {};
              setTryoutModulSelesai(simpanan);
            } catch {}
          }
        } else {
          // Tidak ada tryout aktif (belum waktunya, sudah lewat, atau dinonaktifkan admin)
          // — pastikan kunci latihan harian ikut terlepas, jangan nyangkut di state lama.
          setTryoutAktif(null);
          setTryoutSudah(false);
          setTryoutHasil(null);
          setTryoutPerluBayar(false);
          setTryoutSudahBayar(true);
          setIzinLatihanHarian(false);
          setStatusBayarTryoutSiap(true);
        }
      })
      .catch(() => {}); // gagal cek (mis. jaringan) — statusBayarTryoutSiap SENGAJA
      // tidak diset true di sini, supaya modul tetap dianggap terkunci sampai
      // pengecekan berikutnya (interval 60 detik) berhasil, bukan fail-open.
  };

  useEffect(() => {
    muatStatusTryout();
    // Cek ulang status tryout tiap 60 detik — supaya kunci latihan harian aktif/lepas
    // tepat sesuai jadwal (tanggal_mulai/tanggal_selesai) tanpa perlu reload manual.
    const interval = setInterval(muatStatusTryout, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kalau ada tryout BARU yang jadi aktif (id-nya beda dari sebelumnya), munculkan lagi
  // alert full-screen-nya walau tryout SEBELUMNYA sempat ditutup manual oleh user.
  useEffect(() => {
    setOverlayTryoutDitutup(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tryoutAktif?.id]);

  // Kalau user sudah login sebelumnya (buka lagi aplikasi tanpa login ulang) dan
  // izin notifikasi belum pernah diputuskan, tetap tawarkan otomatis di sini.
  useEffect(() => {
    try {
      if (loggedIn && pushDidukungBrowser() && Notification.permission === 'default') {
        aktifkanPushNotif().catch(() => {});
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBayarTryout = async () => {
    if (!tryoutAktif) return;
    setLoadingBayarTryout(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/tryouts/${tryoutAktif.id}/bayar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.token) {
        alert(data.error || 'Gagal menyiapkan pembayaran. Coba lagi.');
        setLoadingBayarTryout(false);
        // PENTING: refresh status tryout di sini juga — kalau errornya karena
        // "Kamu sudah punya akses ke tryout ini" (mis. dari beri-akses gratis
        // yang beda dari status yang sempat di-fetch waktu halaman ini dibuka),
        // tanpa refresh ini layar paywall bakal nyangkut terkunci selamanya
        // meski backend sebenarnya sudah menganggap user ini punya akses —
        // user harus manual reload halaman untuk lihat modulnya kebuka.
        muatStatusTryout();
        return;
      }
      if (window.snap) {
        window.snap.pay(data.token, {
          onSuccess: () => { muatStatusTryout(); },
          onPending: () => { muatStatusTryout(); },
          onError: () => { alert('Pembayaran gagal. Silakan coba lagi.'); },
          onClose: () => { muatStatusTryout(); },
        });
      }
    } catch {
      alert('Gagal menghubungi server pembayaran.');
    }
    setLoadingBayarTryout(false);
  };

  const rankInfo = getRank(MODULES);
  // Kalau ada tryout aktif yang belum diselesaikan, latihan harian dikunci —
  // user wajib bayar (kalau perlu) lalu menyelesaikan tryout dulu sebelum bisa
  // buka modul latihan harian lagi. Begitu tryoutSudah true, otomatis terbuka.
  const tryoutWajibDiselesaikan = !!(tryoutAktif && !tryoutSudah);
  // Latihan harian terkunci hanya kalau tryout belum selesai DAN admin belum memberi izin.
  const latihanTerkunci = tryoutWajibDiselesaikan && !izinLatihanHarian;
  const handleOpenModule = (mod) => {
    // Sama seperti gate di handleMulaiModulTryout: jangan izinkan modul APAPUN
    // terbuka sebelum status tryout-aktif sempat dikonfirmasi dari server sekali.
    // tryoutWajibDiselesaikan bergantung ke tryoutAktif yang defaultnya null di
    // awal render — sebelum ini dicek, celah ini membuat user bisa buka modul
    // manapun (termasuk yang persis sama dengan modul tryout berbayar) dari tab
    // "Modul" tanpa pernah diarahkan ke layar tryout/paywall sama sekali. Paling
    // kentara di akun yang BARU mendaftar karena belum ada state lama yang cached
    // dari load sebelumnya.
    if (!statusBayarTryoutSiap) return;
    if (latihanTerkunci) { setShowTryoutScreen(true); return; }
    setSelectedModule(mod);
  };

  const handleMulaiModulTryout = (modulId) => {
    // Tolak juga kalau status bayar yang SEBENARNYA belum sempat dicek ke server
    // (statusBayarTryoutSiap masih false) — jangan andalkan default state di awal
    // render, karena defaultnya "seolah sudah bayar" supaya tryout gratis tidak
    // kedip paywall sesaat. Kalau ini tidak dicek, ada window singkat di awal load
    // dimana user bisa mulai modul berbayar sebelum status aslinya sempat masuk.
    if (!statusBayarTryoutSiap) return;
    if (tryoutPerluBayar && !tryoutSudahBayar) return; // masih terkunci, belum bayar
    const mod = MODULES.find(m => m.id === modulId) || { id: modulId };
    setTryoutModeModulId(modulId);
    tryoutModulMulaiAtRef.current = Date.now();
    setShowTryoutScreen(false);
    setSelectedModule(mod);
  };

  const handleTutupTryout = () => setShowTryoutScreen(false);
  const handleBukaTryout = () => setShowTryoutScreen(true);

  const handleSubmitTryout = async () => {
    if (!tryoutAktif) return;
    setLoadingSubmitTryout(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/tryouts/${tryoutAktif.id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ skor_per_modul: tryoutModulSelesai }),
      });
      const data = await res.json();
      if (data.hasil) {
        setTryoutSudah(true);
        setTryoutHasil(data.hasil);
        localStorage.removeItem(`tryoutProgress_${tryoutAktif.id}`);
      }
    } catch {}
    setLoadingSubmitTryout(false);
  };

  const handleBack = async () => {
    if (tryoutModeModulId) {
      const modulId = tryoutModeModulId;
      const mulaiAt = tryoutModulMulaiAtRef.current;
      setSelectedModule(null);
      setTryoutModeModulId(null);
      tryoutModulMulaiAtRef.current = null;
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const res = await fetch(`${API_URL}/api/sesi-latihan`, { headers: { Authorization: `Bearer ${token}` } });
          const data = await res.json();
          const terbaru = (data.sesi || []).find(s => s.modul_id === modulId);
          // PENTING: "terbaru" di sini cuma "sesi paling baru untuk modul ini di
          // seluruh riwayat user", BUKAN otomatis berarti "hasil dari percobaan barusan".
          // Kalau user keluar SEBELUM tesnya benar-benar selesai (mis. tekan back
          // baru 6 detik), tidak ada sesi baru yang tercatat — sehingga "terbaru" di
          // sini bisa jadi sesi LAMA yang tidak berhubungan (dari latihan bebas
          // sebelumnya), lalu skor lama itu keliru ikut tercatat sebagai hasil tryout.
          // Makanya di sini dicek dulu: sesi itu harus benar-benar baru dibuat SETELAH
          // modul ini mulai dikerjakan (dikasih toleransi 5 detik buat selisih jam
          // klien/server), baru dianggap sah sebagai hasil percobaan ini.
          const benarBenarBaru = terbaru && mulaiAt &&
            (new Date(terbaru.dikerjakan_at).getTime() >= mulaiAt - 5000);
          if (benarBenarBaru) {
            const next = { ...tryoutModulSelesai, [modulId]: terbaru.skor };
            setTryoutModulSelesai(next);
            if (tryoutAktif) localStorage.setItem(`tryoutProgress_${tryoutAktif.id}`, JSON.stringify(next));
            // Laporkan progres modul ini ke server (sumber kebenaran untuk status live
            // admin, log progres harian, dan detail benar/salah/kosong di laporan PDF nanti).
            // Di-await (bukan fire-and-forget) supaya baris ini SUDAH tersimpan di DB
            // sebelum kita cek "semua modul selesai?" di bawah dan otomatis submit —
            // kalau tidak, submit bisa jalan sebelum modul terakhir ini benar-benar tercatat.
            if (tryoutAktif) {
              try {
                await fetch(`${API_URL}/api/tryouts/${tryoutAktif.id}/progres-modul`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ modul_id: modulId, skor: terbaru.skor, detail: terbaru.detail }),
                });
              } catch {}
              // Kalau modul yang barusan selesai ini adalah modul TERAKHIR dari seluruh
              // modul resmi tryout (roster dinamis lewat getUrutanModul, tergantung
              // tanggal_mulai tryout ini — lihat modulBaruConfig.js), otomatis kirim
              // hasil — user tidak perlu lagi tekan tombol
              // "Kirim Hasil Tryout" secara manual setelah modul terakhirnya kelar.
              const semuaModulSelesai = getUrutanModul(tryoutAktif).every(m => next[m] !== undefined);
              if (semuaModulSelesai) {
                handleSubmitTryout();
              }
            }
          }
          // Kalau tidak benarBenarBaru: modul ini TIDAK ditandai selesai — biar tetap
          // tampil "belum dikerjakan" di daftar progres tryout, bukan skor keliru.
        } catch {}
      }
      setShowTryoutScreen(true);
      return;
    }
    setSelectedModule(null);
  };

  const handleLogin = (user) => {
    // Cek apakah ini akun berbeda dari yang sebelumnya
    const userLama = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
    if (userLama && userLama.id !== user.id) {
      // Akun berbeda — hapus data progress akun lama
      localStorage.removeItem('jalurUser');
      localStorage.removeItem('riwayatLatihan');
      localStorage.removeItem('riwayatTryout');
      localStorage.removeItem('kraepelin_statistik');
    }
    setUserData(user);
    setLoggedIn(true);
    if (user?.jalur) setJalur(user.jalur);

    // Otomatis minta izin notifikasi begitu user login — best-effort, tidak
    // mengganggu proses login kalau user menolak/browser tidak mendukung.
    // Hanya dicoba kalau izinnya belum pernah diputuskan ('default'); kalau user
    // sebelumnya sudah menolak, browser tidak akan menampilkan popup lagi walau dipaksa.
    try {
      if (pushDidukungBrowser() && Notification.permission === 'default') {
        aktifkanPushNotif().catch(() => {}); // diam kalau ditolak/gagal
      }
    } catch {}
  };

  const handleLogout = () => {
    // Hapus semua data localStorage saat logout
    // supaya akun baru tidak mewarisi data akun lama
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('jalurUser');
    localStorage.removeItem('riwayatLatihan');
    localStorage.removeItem('riwayatTryout');
    localStorage.removeItem('kraepelin_statistik');
    localStorage.removeItem('temaApp');
    setLoggedIn(false);
    setUserData(null);
    setTab('beranda');
    setSelectedModule(null);
  };

  const handlePilihJalur = (j) => {
    setJalur(j);
    setGantiJalurMode(false);
    // Simpan juga ke server supaya tersinkron kalau user login dari device lain
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API_URL}/api/profil/jalur`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ jalur: j }),
      }).then(async (r) => {
        const data = await r.json();
        if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
      }).catch(() => {});
    }
  };
  const handleGantiJalur = () => setGantiJalurMode(true);

  // ── Lapor ke Admin kalau lagi aktif ngerjain modul (Aktivitas Live) ──
  const MODUL_AKTIF_LIVE = ['kraepelin', 'kecermatan', 'logika', 'kepribadian', 'figural', 'armyalpha', 'akademik', 'verbal', 'mekanik', 'substitusi', 'kestabilan', 'interpretasigambar', 'interpretasibentuk'];
  useEffect(() => {
    const modulId = selectedModule?.id;
    if (!modulId || !MODUL_AKTIF_LIVE.includes(modulId)) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    // Kalau modul ini sedang dikerjakan DALAM konteks tryout resmi, ikut kirim
    // tryout_id-nya — supaya Admin Panel bisa tandai "sedang tryout" (bukan cuma
    // latihan biasa) dan tahu ini bagian dari tryout yang mana.
    const tryoutIdKonteks = (tryoutModeModulId === modulId && tryoutAktif) ? tryoutAktif.id : null;

    const kirimHeartbeat = () => {
      fetch(`${API_URL}/api/aktivitas/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ modul_id: modulId, tryout_id: tryoutIdKonteks }),
      }).catch(() => {});
    };

    kirimHeartbeat(); // langsung kirim begitu modul dibuka
    const interval = setInterval(kirimHeartbeat, 30000); // lalu tiap 30 detik

    return () => {
      clearInterval(interval);
      // Lapor berhenti begitu user keluar dari modul ini
      fetch(`${API_URL}/api/aktivitas/berhenti`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      }).catch(() => {});
    };
  }, [selectedModule?.id, tryoutModeModulId, tryoutAktif]);

  let content;
  if (selectedModule && selectedModule.id === 'kraepelin') {
    content = <KraepelinModul onBack={handleBack} tryoutMode={tryoutModeModulId === 'kraepelin'} tryoutId={tryoutModeModulId === 'kraepelin' ? tryoutAktif?.id : null} />;
  } else if (selectedModule && selectedModule.id === 'kecermatan') {
    content = <KecermatanModul onBack={handleBack} tryoutMode={tryoutModeModulId === 'kecermatan'} tryoutId={tryoutModeModulId === 'kecermatan' ? tryoutAktif?.id : null} tryoutTanggalMulai={tryoutModeModulId === 'kecermatan' ? tryoutAktif?.tanggal_mulai : null} />;
  } else if (selectedModule && selectedModule.id === 'logika') {
    content = <LogikaModul onBack={handleBack} tryoutMode={tryoutModeModulId === 'logika'} tryoutId={tryoutModeModulId === 'logika' ? tryoutAktif?.id : null} />;
  } else if (selectedModule && selectedModule.id === 'kepribadian') {
    content = <KepribadianModul onBack={handleBack} tryoutMode={tryoutModeModulId === 'kepribadian'} tryoutId={tryoutModeModulId === 'kepribadian' ? tryoutAktif?.id : null} />;
  } else if (selectedModule && selectedModule.id === 'figural') {
    content = <FiguralModul onBack={handleBack} tryoutMode={tryoutModeModulId === 'figural'} tryoutId={tryoutModeModulId === 'figural' ? tryoutAktif?.id : null} />;
  } else if (selectedModule && selectedModule.id === 'armyalpha') {
    content = <ArmyAlphaModul onBack={handleBack} tryoutMode={tryoutModeModulId === 'armyalpha'} tryoutId={tryoutModeModulId === 'armyalpha' ? tryoutAktif?.id : null} />;
  } else if (selectedModule && selectedModule.id === 'akademik') {
    content = <AkademikModul onBack={handleBack} tryoutMode={tryoutModeModulId === 'akademik'} tryoutId={tryoutModeModulId === 'akademik' ? tryoutAktif?.id : null} />;
  } else if (selectedModule && selectedModule.id === 'verbal') {
    content = <VerbalModul onBack={handleBack} tryoutMode={tryoutModeModulId === 'verbal'} tryoutId={tryoutModeModulId === 'verbal' ? tryoutAktif?.id : null} />;
  } else if (selectedModule && selectedModule.id === 'mekanik') {
    content = <MekanikModul onBack={handleBack} tryoutMode={tryoutModeModulId === 'mekanik'} tryoutId={tryoutModeModulId === 'mekanik' ? tryoutAktif?.id : null} />;
  } else if (selectedModule && selectedModule.id === 'substitusi') {
    content = <SubstitusiModul onBack={handleBack} tryoutMode={tryoutModeModulId === 'substitusi'} tryoutId={tryoutModeModulId === 'substitusi' ? tryoutAktif?.id : null} />;
  } else if (selectedModule && selectedModule.id === 'kestabilan') {
    content = <KestabilanModul onBack={handleBack} tryoutMode={tryoutModeModulId === 'kestabilan'} tryoutId={tryoutModeModulId === 'kestabilan' ? tryoutAktif?.id : null} />;
  } else if (selectedModule && selectedModule.id === 'interpretasigambar') {
    content = <InterpretasiGambarModul onBack={handleBack} tryoutMode={tryoutModeModulId === 'interpretasigambar'} tryoutId={tryoutModeModulId === 'interpretasigambar' ? tryoutAktif?.id : null} />;
  } else if (selectedModule && selectedModule.id === 'interpretasibentuk') {
    content = <InterpretasiBentukModul onBack={handleBack} tryoutMode={tryoutModeModulId === 'interpretasibentuk'} tryoutId={tryoutModeModulId === 'interpretasibentuk' ? tryoutAktif?.id : null} />;
  } else if (selectedModule) {
    content = <ModuleDetailScreen mod={selectedModule} onBack={handleBack} />;
  } else if (showTryoutScreen && tryoutAktif) {
    content = (
      <TryoutScreen
        tryout={tryoutAktif}
        modulSelesai={tryoutModulSelesai}
        onMulaiModul={handleMulaiModulTryout}
        onSubmit={handleSubmitTryout}
        onTutup={handleTutupTryout}
        sudahDikerjakan={tryoutSudah}
        hasilAkhir={tryoutHasil}
        loadingSubmit={loadingSubmitTryout}
        // Selama status bayar asli belum sempat dicek ke server (statusBayarTryoutSiap
        // masih false), paksa dianggap "berbayar & belum lunas" supaya TryoutScreen
        // ikut mengunci checklist modulnya juga (defense in depth, selaras dengan
        // guard yang sama di handleMulaiModulTryout) — bukan fail-open ke status
        // default yang seolah "sudah lunas".
        perluBayar={statusBayarTryoutSiap ? tryoutPerluBayar : true}
        sudahBayar={statusBayarTryoutSiap ? tryoutSudahBayar : false}
        onBayar={handleBayarTryout}
        loadingBayar={loadingBayarTryout}
      />
    );
  } else if (tab === 'beranda') {
    content = <BerandaScreen rankInfo={rankInfo} onOpenModule={handleOpenModule} tryoutAktif={tryoutAktif} tryoutSudah={tryoutSudah} tryoutWajib={tryoutWajibDiselesaikan} latihanTerkunci={latihanTerkunci} tryoutPerluBayar={tryoutPerluBayar} tryoutSudahBayar={tryoutSudahBayar} onBukaTryout={handleBukaTryout} onLihatProfilLengkap={() => setTab('profil')} />;
  } else if (tab === 'modul') {
    content = <ModulScreen onOpenModule={handleOpenModule} tryoutWajib={tryoutWajibDiselesaikan} latihanTerkunci={latihanTerkunci} tryoutPerluBayar={tryoutPerluBayar} tryoutSudahBayar={tryoutSudahBayar} onBukaTryout={handleBukaTryout} />;
  } else if (tab === 'riwayat') {
    content = <RiwayatScreen />;
  } else {
    content = <ProfilScreen rankInfo={rankInfo} onLogout={handleLogout} onGantiJalur={handleGantiJalur} />;
  }

  // Tampilkan layar pilih jalur jika: belum pernah pilih, atau sedang mode ganti jalur
  const tampilkanPilihJalur = loggedIn && (!jalur || gantiJalurMode);

  return (
    <div className="w-full h-screen flex items-center justify-center font-sans"
         style={{ backgroundColor: 'var(--bg-app)' }}>
      <div className="w-full max-w-[400px] h-full max-h-[860px] rounded-none sm:rounded-[28px] overflow-hidden flex flex-col shadow-2xl relative"
           style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}>
        {!loggedIn ? (
          <LoginScreen onLogin={handleLogin} />
        ) : tampilkanPilihJalur ? (
          <LayarPilihJalur onPilih={handlePilihJalur} />
        ) : (
          <>
            <div className="flex-1 overflow-y-auto">{content}</div>
            <BottomNav active={tab} onChange={(t) => { setTab(t); setSelectedModule(null); }} />
          </>
        )}

        {/* Alert full-screen: tryout wajib diselesaikan, latihan harian terkunci */}
        {loggedIn && !tampilkanPilihJalur && latihanTerkunci && !showTryoutScreen && !overlayTryoutDitutup && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center px-6 text-center"
               style={{ backgroundColor: 'rgba(3,23,15,0.97)' }}>
            <button onClick={() => setOverlayTryoutDitutup(true)}
              className="absolute top-5 right-5 w-9 h-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
              <X size={16} style={{ color: 'var(--text-muted)' }} />
            </button>

            <div className="w-20 h-20 rounded-full flex items-center justify-center mb-5"
                 style={{ backgroundColor: 'rgba(220,38,38,0.15)', border: '2px solid var(--danger)' }}>
              <Lock size={34} style={{ color: 'var(--danger)' }} />
            </div>
            <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--danger)' }}>Tryout Wajib Diselesaikan</p>
            <h2 className="text-2xl font-bold mb-3 leading-snug" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>
              {tryoutAktif?.judul}
            </h2>
            <p className="text-sm mb-8 max-w-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
              Semua latihan harian terkunci sementara.{' '}
              {tryoutPerluBayar && !tryoutSudahBayar
                ? 'Bayar lalu kerjakan tryout ini sampai selesai untuk membukanya kembali.'
                : 'Kerjakan tryout ini sampai selesai untuk membukanya kembali.'}
            </p>

            <button onClick={() => { setShowTryoutScreen(true); setOverlayTryoutDitutup(true); }}
              className="w-full max-w-xs py-4 rounded-xl font-bold text-base"
              style={{ backgroundColor: 'var(--danger)', color: '#fff' }}>
              {tryoutPerluBayar && !tryoutSudahBayar ? 'Bayar & Kerjakan Sekarang' : 'Kerjakan Sekarang'}
            </button>
            <button onClick={() => setOverlayTryoutDitutup(true)}
              className="mt-4 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
              Lihat dashboard dulu
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Halaman Verifikasi Email ─────────────────────────────────
function HalamanVerifikasi() {
  const [status, setStatus] = useState('loading'); // loading | sukses | gagal
  const [pesan, setPesan]   = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token  = params.get('token');
    if (!token) { setStatus('gagal'); setPesan('Token tidak valid'); return; }

    fetch(`${API_URL}/api/verifikasi?token=${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.verified) { setStatus('sukses'); setPesan(data.message); }
        else { setStatus('gagal'); setPesan(data.error || 'Verifikasi gagal'); }
      })
      .catch(() => { setStatus('gagal'); setPesan('Tidak bisa terhubung ke server'); });
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: 'var(--bg-app)' }}>
      <div className="w-full max-w-sm text-center p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}>
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p style={{ color: 'var(--text-primary)' }}>Memverifikasi email...</p>
          </>
        )}
        {status === 'sukses' && (
          <>
            <span className="text-5xl mb-4 block">✅</span>
            <h2 className="text-xl font-bold mb-2" style={{ color: '#4ADE80', fontFamily: "'Poppins', sans-serif" }}>EMAIL TERVERIFIKASI!</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>{pesan}</p>
            <a href="/" className="block w-full py-3 rounded-xl font-bold text-sm uppercase"
               style={{ backgroundColor: 'var(--accent)', color: 'var(--bg-app)', fontFamily: "'Poppins', sans-serif" }}>
              Login Sekarang →
            </a>
          </>
        )}
        {status === 'gagal' && (
          <>
            <span className="text-5xl mb-4 block">❌</span>
            <h2 className="text-xl font-bold mb-2" style={{ color: '#F87171', fontFamily: "'Poppins', sans-serif" }}>VERIFIKASI GAGAL</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>{pesan}</p>
            <a href="/" className="block w-full py-3 rounded-xl font-bold text-sm uppercase"
               style={{ backgroundColor: 'var(--bg-card2)', color: 'var(--accent)', border: '1px solid var(--border)', fontFamily: "'Poppins', sans-serif" }}>
              Kembali ke Beranda
            </a>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Halaman Reset Password ───────────────────────────────────
function HalamanResetPassword() {
  const [password, setPassword]     = useState('');
  const [konfirmasi, setKonfirmasi] = useState('');
  const [status, setStatus]         = useState('form');
  const [pesan, setPesan]           = useState('');
  const token = new URLSearchParams(window.location.search).get('token');

  const handleReset = async () => {
    if (!password || !konfirmasi) return setPesan('Semua kolom wajib diisi');
    if (password !== konfirmasi) return setPesan('Password tidak cocok');
    if (password.length < 6) return setPesan('Password minimal 6 karakter');
    setStatus('loading');
    try {
      const res  = await fetch(`${API_URL}/api/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password_baru: password }),
      });
      const data = await res.json();
      if (data.message) { setStatus('sukses'); setPesan(data.message); }
      else { setStatus('gagal'); setPesan(data.error || 'Reset gagal'); }
    } catch { setStatus('gagal'); setPesan('Tidak bisa terhubung ke server'); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: 'var(--bg-app)' }}>
      <div className="w-full max-w-sm p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}>
        {status === 'sukses' ? (
          <div className="text-center">
            <span className="text-5xl mb-4 block">✅</span>
            <h2 className="text-xl font-bold mb-2" style={{ color: '#4ADE80', fontFamily: "'Poppins', sans-serif" }}>PASSWORD BERHASIL DIRESET!</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>{pesan}</p>
            <a href="/" className="block w-full py-3 rounded-xl font-bold text-sm uppercase text-center"
               style={{ backgroundColor: 'var(--accent)', color: 'var(--bg-app)', fontFamily: "'Poppins', sans-serif" }}>
              Login Sekarang →
            </a>
          </div>
        ) : status === 'gagal' ? (
          <div className="text-center">
            <span className="text-5xl mb-4 block">❌</span>
            <h2 className="text-xl font-bold mb-2" style={{ color: '#F87171', fontFamily: "'Poppins', sans-serif" }}>RESET GAGAL</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>{pesan}</p>
            <a href="/" className="block w-full py-3 rounded-xl font-bold text-sm uppercase text-center"
               style={{ backgroundColor: 'var(--bg-card2)', color: 'var(--accent)', border: '1px solid var(--border)' }}>
              Kembali ke Beranda
            </a>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <span className="text-4xl mb-3 block">🔐</span>
              <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>BUAT PASSWORD BARU</h2>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Masukkan password baru untuk akunmu</p>
            </div>
            {pesan && (
              <div className="mb-4 px-4 py-3 rounded-xl text-xs" style={{ backgroundColor: '#3A2020', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' }}>
                {pesan}
              </div>
            )}
            <div className="space-y-3 mb-4">
              <div>
                <label className="text-xs uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Password Baru</label>
                <div className="flex items-center gap-2 rounded-lg px-3 py-3" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>🔒</span>
                  <input type="password" placeholder="Minimal 6 karakter" value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="bg-transparent outline-none text-sm w-full" style={{ color: 'var(--text-primary)' }} />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Konfirmasi Password</label>
                <div className="flex items-center gap-2 rounded-lg px-3 py-3" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>🔒</span>
                  <input type="password" placeholder="Ulangi password baru" value={konfirmasi}
                    onChange={e => setKonfirmasi(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleReset()}
                    className="bg-transparent outline-none text-sm w-full" style={{ color: 'var(--text-primary)' }} />
                </div>
              </div>
            </div>
            <button onClick={handleReset} disabled={status === 'loading'}
              className="w-full py-3.5 rounded-xl font-bold text-sm uppercase"
              style={{ backgroundColor: 'var(--accent)', color: 'var(--bg-app)', fontFamily: "'Poppins', sans-serif", opacity: status === 'loading' ? 0.7 : 1 }}>
              {status === 'loading' ? 'Menyimpan...' : 'Simpan Password Baru →'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const path = window.location.pathname;
  // Halaman-halaman ini dulu di-return duluan SEBELUM masuk <ThemeProvider>,
  // jadi tidak pernah ikut dark/light mode sistem (selalu terang). Sekarang
  // semua tetap dibungkus ThemeProvider supaya konsisten dengan sisi user —
  // hanya JalurProvider yang sengaja tidak ikut karena halaman-halaman ini
  // tidak butuh data jalur (Tamtama/Bintara).
  if (path === '/admin') return <ThemeProvider><AdminPanel /></ThemeProvider>;
  if (path === '/subadmin') return <ThemeProvider><SubAdminPanel /></ThemeProvider>;
  if (path === '/verifikasi') return <ThemeProvider><HalamanVerifikasi /></ThemeProvider>;
  if (path === '/reset-password') return <ThemeProvider><HalamanResetPassword /></ThemeProvider>;

  return (
    <ThemeProvider>
      <JalurProvider>
        <AppContent />
      </JalurProvider>
    </ThemeProvider>
  );
}
